/**
 * InstallAppButton — universal PWA install CTA.
 *
 * Behaviour by platform:
 *   • Android / Desktop Chrome / Edge: intercepts `beforeinstallprompt` and
 *     triggers the native install dialog on click.
 *   • iOS Safari (no beforeinstallprompt support): shows an in-app modal
 *     with the "Share ▸ Add to Home Screen" instructions.
 *   • Already installed / running in standalone mode: hides itself.
 *
 * Props:
 *   variant   'hero' | 'header' | 'card'  → styling preset
 *   className additional Tailwind classes to compose
 *   label     optional override for the button text
 */
import React, { useEffect, useState, useCallback } from 'react';
import { FaMobileAlt, FaTimes, FaShareSquare, FaPlusSquare } from 'react-icons/fa';

const isIos = () => {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari
    window.navigator.standalone === true
  );
};

const InstallAppButton = ({ variant = 'header', className = '', label }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [showIosModal, setShowIosModal] = useState(false);
  const ios = isIos();

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const handleInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const handleClick = useCallback(async () => {
    if (ios) {
      setShowIosModal(true);
      return;
    }
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setInstalled(true);
      setDeferredPrompt(null);
      return;
    }
    // Fallback: no native prompt available yet — surface the iOS-style modal
    // with generic instructions so the user still knows how to install.
    setShowIosModal(true);
  }, [deferredPrompt, ios]);

  if (installed) return null;

  const presets = {
    hero:
      'inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0A1929] font-bold rounded-xl hover:shadow-lg hover:shadow-[#FFD700]/40 transition-all',
    header:
      'inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FFD700]/10 border border-[#FFD700]/40 text-[#FFD700] rounded-lg text-xs font-semibold hover:bg-[#FFD700]/20 transition-colors',
    card:
      'inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0A1929] font-bold rounded-lg hover:shadow-lg hover:shadow-[#FFD700]/30 transition-all',
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid="install-app-btn"
        className={`${presets[variant] || presets.header} ${className}`}
        aria-label="Install Arth Gurukul as a mobile app"
      >
        <FaMobileAlt />
        <span>{label || 'Install App'}</span>
      </button>

      {showIosModal && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowIosModal(false)}
          data-testid="install-instructions-modal"
        >
          <div
            className="bg-gradient-to-br from-[#1A2942] to-[#0F172A] border border-[#FFD700]/40 rounded-2xl max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-[#2D5A8F]/60">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#FFD700] flex items-center justify-center">
                  <FaMobileAlt className="text-[#0A1929]" size={20} />
                </div>
                <div>
                  <p className="text-white font-bold text-base leading-tight">Install Arth Gurukul</p>
                  <p className="text-gray-400 text-xs">Add it to your home screen</p>
                </div>
              </div>
              <button
                onClick={() => setShowIosModal(false)}
                className="text-gray-400 hover:text-white"
                data-testid="install-modal-close"
              >
                <FaTimes />
              </button>
            </div>

            <div className="p-5 space-y-4 text-sm">
              {ios ? (
                <>
                  <p className="text-gray-300">Follow these two steps in Safari:</p>
                  <ol className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#FFD700] text-[#0A1929] font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                      <span className="text-gray-200">
                        Tap the <FaShareSquare className="inline text-[#FFD700] mx-1" /> <b>Share</b> icon at the bottom of your screen.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#FFD700] text-[#0A1929] font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                      <span className="text-gray-200">
                        Scroll and choose <FaPlusSquare className="inline text-[#FFD700] mx-1" /> <b>Add to Home Screen</b>, then tap <b>Add</b>.
                      </span>
                    </li>
                  </ol>
                </>
              ) : (
                <>
                  <p className="text-gray-300">Your browser hasn&apos;t offered the install prompt yet. You can still install manually:</p>
                  <ol className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#FFD700] text-[#0A1929] font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                      <span className="text-gray-200">Open your browser menu (the <b>⋮</b> or <b>⋯</b> icon).</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#FFD700] text-[#0A1929] font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                      <span className="text-gray-200">Tap <b>Install app</b> or <b>Add to Home Screen</b>.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#FFD700] text-[#0A1929] font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                      <span className="text-gray-200">Confirm to add the Arth Gurukul icon to your home screen.</span>
                    </li>
                  </ol>
                </>
              )}
              <p className="text-gray-500 text-xs pt-2">
                Once installed, Arth Gurukul opens like a native app — full screen, no browser bar.
              </p>
            </div>

            <div className="p-4 pt-2">
              <button
                onClick={() => setShowIosModal(false)}
                className="w-full py-2.5 bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0A1929] font-bold rounded-lg text-sm"
                data-testid="install-modal-got-it"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallAppButton;
